<script setup lang="ts">
import { signOfMoney } from '@taler/domain'
import { EmptyState } from '@taler/ui'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import MoneyAmount from '@/app/components/MoneyAmount.vue'
import { hasCapability, useSession } from '@/app/session'
import { canForgiveContact } from '@/features/forgiveness'
import type { LentToItem } from '../internal/view-types.ts'

const props = defineProps<{ rows: readonly LentToItem[] }>()
const emit = defineEmits<{ forgive: [row: LentToItem] }>()
const { t } = useI18n()
const session = useSession()

/** `undefined`: show the action. A string: disabled, with that reason. `null`: nothing owed, omit it. */
function forgiveState(row: LentToItem): string | undefined | null {
  if (signOfMoney(row.owed) <= 0) return null
  const target = row.target
  if (target.kind === 'contact') {
    return canForgiveContact(session.can) ? undefined : null
  }
  if (!session.can('TRANSFER')) return t('reporting.lentTo.noTransferHere')
  const other = session.ledgers.value.find((ledger) => ledger.id === target.otherLedgerId)
  if (!hasCapability(other, 'TRANSFER')) return t('reporting.lentTo.noTransferThere')
  return undefined
}

const visibleRows = computed(() => props.rows)
</script>

<template>
  <EmptyState
    v-if="visibleRows.length === 0"
    :title="t('reporting.lentTo.empty.title')"
    :body="t('reporting.lentTo.empty.body')"
  />
  <div
    v-else
    class="overflow-x-auto rounded-[var(--radius-surface)] border border-[color:var(--color-border)] bg-[color:var(--color-surface)]"
  >
    <table class="w-full border-collapse text-left text-[length:var(--text-step-2)]">
      <caption class="sr-only">
        {{
          t('reporting.lentTo.title')
        }}
      </caption>
      <thead>
        <tr class="table-head">
          <th scope="col" class="px-[var(--space-group)] py-[var(--space-inline)]">
            {{ t('reporting.lentTo.name') }}
          </th>
          <th scope="col" class="px-[var(--space-group)] py-[var(--space-inline)] text-right">
            {{ t('reporting.lentTo.owed') }}
          </th>
          <th scope="col" class="px-[var(--space-group)] py-[var(--space-inline)] text-right">
            {{ t('reporting.lentTo.repaid') }}
          </th>
          <th scope="col" class="px-[var(--space-group)] py-[var(--space-inline)] text-right">
            {{ t('reporting.lentTo.forgiven') }}
          </th>
          <th scope="col" class="px-[var(--space-group)] py-[var(--space-inline)] text-right">
            <span class="sr-only">{{ t('reporting.lentTo.actions') }}</span>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="(row, index) in visibleRows"
          :key="index"
          class="border-t border-[color:var(--color-border)]"
        >
          <th
            scope="row"
            class="px-[var(--space-group)] py-[var(--space-inline)] font-medium text-[color:var(--color-text)]"
          >
            {{ row.name }}
          </th>
          <td class="px-[var(--space-group)] py-[var(--space-inline)] text-right">
            <MoneyAmount :value="row.owed" />
          </td>
          <td class="px-[var(--space-group)] py-[var(--space-inline)] text-right">
            <MoneyAmount :value="row.repaid" />
          </td>
          <td class="px-[var(--space-group)] py-[var(--space-inline)] text-right">
            <MoneyAmount :value="row.forgiven" />
          </td>
          <td class="px-[var(--space-group)] py-[var(--space-inline)] text-right">
            <button
              v-if="forgiveState(row) !== null"
              type="button"
              class="min-h-[var(--density-target-min)] rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] px-[var(--space-cell)] text-[length:var(--text-step-2)] text-[color:var(--color-text)] disabled:opacity-60"
              :disabled="forgiveState(row) !== undefined"
              :title="forgiveState(row) ?? undefined"
              @click="emit('forgive', row)"
            >
              {{ t('reporting.lentTo.forgive') }}
            </button>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>
